import ViewerCommunication from '@meddream/viewer-communication';
import uniqId from 'uniqid';
import waitFor from '../helpers/waitFor';
import { isResearch } from '@/utils/routing/routing';

const COLOR_TAG_STUDY = '#F78F87';
const COLOR_TAG_SERIE = '#F78F87';

function getViewerTags() {
  const series = this.openStudies.flatMap((study) => study.seriesImages);
  const seriesTags = series?.map((seriesImage) => ({
    uid: seriesImage.seriesUid,
    level: 'series',
    hideOriginalLabels: false,
    labels: seriesImage.customTags?.map((tag) => tag.value),
  }));

  const customTags = [
    ...this.openStudies?.map(({ studyInstanceUid, modalities = [], seriesImages }) => ({
      level: 'study',
      uid: studyInstanceUid,
      tags: modalities
        .map((modality) => ({ text: modality, color: COLOR_TAG_STUDY }))
        .concat(
          seriesImages?.flatMap((seriesImage) =>
            seriesImage.customTags.map((tag) => ({ text: tag.value, color: COLOR_TAG_SERIE }))
          )
        ),
    })),
    ...seriesTags?.map(({ uid, level = 'series', labels }) => ({
      level,
      uid,
      tags: labels?.map((label) => ({ text: label, color: COLOR_TAG_SERIE })),
    })),
  ];

  return {
    seriesTags,
    customTags,
  };
}

export class ViewerPopup {
  construct(onClose = () => {}) {
    this.lastBaseUrl = null;
    this.popup = null;
    this.currentUrl = null;
    this.openStudies = [];
    this.communication = null;
    this.currentToken = null;
    this.images = null;
    this.isOpen = null;
    this.isMessagingReady = false;
    this.currentOptions = {};
    this.segmentationSubscribed = false;
    this.onClose = onClose;
  }

  prepareMeddream(baseUrl, token, onReady = () => {}) {
    if (this.lastBaseUrl !== baseUrl) {
      this.communication = new ViewerCommunication(baseUrl, 'token');
    }
    this.communication.openInMedDreamWindow(token);
    this.popup = this.communication.getWindowReference();

    this.communication.focusWindow();

    // communication.registerMessageReceivedEvent();
    //Method has been called on meddream viewer instance create, on the viewer side
    //so for now there's no need to call it from the PopupManager.

    this.communication.subscribeCommunicationServiceReadyEvent(() => {
      console.log('[meddream] communication ready');
      this.isMessagingReady = true;
      onReady();
      this.communication.subscribeStudyLoadedEvent(() => {
        if (isResearch()) {
          const { seriesTags, customTags } = getViewerTags.call(this);
          this.communication.setAdditionalInfoLabels({
            topLeftLabels: seriesTags,
          });
          this.communication.setCustomTags(customTags);
        }
        this.getOpenStudies();
      });
    });

    window.addEventListener('beforeunload', () => {
      this.closeWindow();
      this.onClose();
    });

    return this.popup;
  }

  isPopupOpen() {
    return this.popup && !this.popup.closed;
  }

  async openWindow(url, studies, token, options = {}, keyImages, appSection) {
    this.currentOptions = options;

    const openUrl = new URL(url);
    const baseUrl = openUrl.origin;

    if (!window) {
      return this;
    }

    if (appSection === 'research') {
      const segmentationPermissions = {
        boundingBox: options.segmentationOptions?.boundingBox,
        freeDraw: options.segmentationOptions?.freeDraw,
        smartDraw: options.segmentationOptions?.smartDraw,
      };

      if (!this.isPopupOpen()) {
        this.popup = this.prepareMeddream(baseUrl, token, () => {
          this.setSegmentationPermissions(
            segmentationPermissions.boundingBox,
            segmentationPermissions.freeDraw,
            segmentationPermissions.smartDraw
          );
        });
      } else if (this.currentUrl !== url) {
        this.replaceStudies(studies, token, options);
        setTimeout(
          () =>
            this.setSegmentationPermissions(
              segmentationPermissions.boundingBox,
              segmentationPermissions.freeDraw,
              segmentationPermissions.smartDraw
            ),
          1000
        );
      }

      this.openStudies = studies;
      this.currentUrl = url;
      this.currentToken = token;
    } else {
      if (window) {
        this.images = keyImages.filter((keyImage) => !keyImage.originReply);

        this.images = keyImages.filter((keyImage) => !keyImage.originReply);

        if (!this.isPopupOpen()) {
          this.popup = this.prepareMeddream(baseUrl, token, () => {
            this.communication.subscribeGetOpenedStudiesEvent((studies) => {
              console.log('[meddream] got openened studies', { studies });
              this.setViewerLayout(this.images);
            });
          });
        } else if (this.currentUrl !== url) {
          this.replaceStudies(studies, token, options);
        }

        this.isOpen = false;
        this.openStudies = studies;
        this.currentUrl = url;
        this.currentToken = token;
      }

      return this;
    }
  }

  closeWindow() {
    if (this.popup && !this.popup.closed) {
      this.communication.unsubscribeCommunicationServiceReadyEvent();
      this.communication.unsubscribeStudyLoadedEvent();
      this.communication.unsubscribeAnnotationsSavedEvent();
      this.popup.close();
    }

    this.isMessagingReady = false;
    this.segmentationSubscribed = false;

    this.popup = null;
    this.currentUrl = null;
    this.currentToken = null;
    this.openStudies = [];
  }

  openAdditionalStudies(studies, token, options = {}) {
    this.communication.openStudies(token);
    this.openStudies = [...openStudies, ...studies];
    this.currentToken = token;

    this.getOpenStudies();
  }

  replaceStudies(studies = [], token, options = {}) {
    this.openStudies = [...studies];
    this.currentToken = token;
    this.communication.replaceStudies(token);
  }

  preloadStudies(studies, token, options = {}) {
    this.communication.preloadStudies(token);
  }

  openInstance(uid, row, col, frame = null) {
    // meddream expects instanceId to be in format: uid:frame when the images are multiframe
    const instanceId = frame ? `${uid}:${frame}` : uid;
    this.communication.openInstance(instanceId, col, row);
  }

  setLayout(row, col) {
    this.communication.setLayout(col, row);
  }

  setViewerLayout(images) {
    console.log('[meddream] setting layout');

    if (images && images.length) {
      let layout = this.calculateLayout(images.slice(0, 6));
      this.setLayout(layout.rows, layout.cols);
      let i = 0;
      for (let row = 1; row <= layout.rows; row++) {
        for (let col = 1; col <= layout.cols; col++) {
          this.openInstance(images[i].sopInstanceUid, row, col, images[i].key_frame);
          i++;
        }
      }
    } else {
      this.setLayout(1, 1);
    }
  }

  calculateLayout(images) {
    switch (images.length) {
      case 1:
        return { rows: 1, cols: 1 };
      case 2:
        return { rows: 1, cols: 2 };
      case 3:
        return { rows: 1, cols: 3 };
      case 4:
        return { rows: 2, cols: 2 };
      case 5:
        return { rows: 2, cols: 2 };
      case 6:
        return { rows: 2, cols: 3 };
      default:
        return { rows: 1, cols: 1 };
    }
  }

  closeStudies(studies, token, options = {}) {
    if (this.currentToken === token) {
      this.communication.closeAllStudies();
      this.openStudies = this.openStudies.filter((study) => !studies.map((st) => st.id).includes(study.id));
      this.currentToken = null;

      return true;
    }

    return false;
  }

  onMessageReceived({ data, origin, ...args }) {
    const meddreamUrl = new URL(this.currentUrl);
    if (origin === meddreamUrl.origin) {
      console.log('Message received from meddream', { data, origin, ...args });
      if (data?.actionType === 'COMMUNICATION_SERVICE_READY' && !this.isOpen) {
        this.isOpen = true;
        this.isMessagingReady = true;
      }
      if (data?.actionType === 'STUDY_LOADED') {
        this.getOpenStudies();
      }
    }
    if (data?.actionType === 'GET_OPENED_STUDIES' && !this.isOpen) {
      this.isOpen = !this.isOpen;
      const url = new URL(this.currentUrl);

      if (origin === url.origin) {
        this.setViewerLayout(this.images);
      }
    }
  }

  getOpenStudies() {
    this.communication.getOpenedStudies();
  }

  isReadOnly() {
    return this.currentOptions?.readOnly ?? false;
  }

  setSegmentationPermissions(boundingBox = true, freeDraw = true, smartDraw = true) {
    const permissions = {
      boundingBoxView: boundingBox,
      boundingBox2dEdit: boundingBox && !this.isReadOnly(),
      boundingBox3dEdit: boundingBox && !this.isReadOnly(),
      boundingBoxInfo: boundingBox,
      freeDrawView: freeDraw,
      freeDrawEdit: freeDraw && !this.isReadOnly(),
      freeDrawInfo: freeDraw,
      smartPaintView: smartDraw,
      smartPaint2dEdit: smartDraw && !this.isReadOnly(),
      smartPaint3dEdit: smartDraw && !this.isReadOnly(),
      smartPaintInfo: smartDraw,
    };

    this.communication.updateSegmentationToolPermissions(permissions);

    if (!this.isReadOnly()) {
      this.subscribeSegmentationSave();
    }
  }

  async sendMessage(msg, data = {}) {
    console.warn(
      '[meddream] direct communication with meddream is deprecated, please use viewer-communication library'
    );
    if (!this.isMessagingReady) {
      await waitFor(() => {
        console.log('Waiting for meddream to be ready...');

        return this.isMessagingReady;
      });
    }

    const url = new URL(this.currentUrl);
    this.popup.postMessage(
      {
        actionType: msg,
        actionData: data,
      },
      url.origin
    );

    console.log('message sent to meddream', {
      actionType: msg,
      actionData: data,
    });
  }

  subscribeSegmentationSave() {
    const callback = this.currentOptions.onSegmentation ?? null;

    if (callback) {
      if (this.currentOptions.isSegmentationSaveStartedEventSubscriptionEnabled) {
        console.log('[meddream] subscribing to segmentation save started event');
        this.communication.subscribeAnnotationsSaveStartedEvent((segmentationData) => {
          console.log('[meddream] segmentation save started received', { segmentationData, callback });
          callback({
            event: 'segmentationSaveStarted',
            segmentationData,
          });
        });
      }

      console.log('[meddream] subscribing to segmentation saved event');
      this.communication.subscribeAnnotationsSavedEvent((segmentationData) => {
        callback({
          event: 'segmentationSaved',
          segmentationData,
        });
      });
    }

    if (!callback) {
      this.communication.unsubscribeAnnotationsSavedEvent();
      if (this.currentOptions.isSegmentationSaveStartedEventSubscriptionEnabled) {
        this.communication.unsubscribeAnnotationsSaveStartedEvent();
      }
    }
  }

  getWindowReference() {
    return this.popup;
  }
}

export class ViewerPopupManager {
  constructor() {
    this.popups = {};
  }

  getPopupManager(id = uniqId()) {
    if (!this.popups[id]) {
      this.popups[id] = new ViewerPopup(() => {
        delete this.popups[id];
      });
    }

    return this.popups[id];
  }

  getSinglePopupManager() {
    return this.getPopupManager('single');
  }
}

export default new ViewerPopupManager().getSinglePopupManager();

export const SingleViewerManager = new ViewerPopupManager().getSinglePopupManager();
export const MultipleViewerManager = new ViewerPopupManager();
